import { addDaysToDate, compareDates } from '../dateString.js';
export function unit() {
    return { kind: 'unit' };
}
export function scaleAmount(factor, amount) {
    return { kind: 'scale', factor: { kind: 'const', value: factor }, amount };
}
export function scaleAmountBy(factor, amount) {
    return { kind: 'scale', factor, amount };
}
export function amountOf(value) {
    return { kind: 'scale', factor: value, amount: unit() };
}
export function giveAmount(amount) {
    return { kind: 'give', amount };
}
export function whenAmount(condition, amount) {
    return { kind: 'when', condition, amount };
}
export function allAmounts(...amounts) {
    if (amounts.length === 0)
        return unit();
    if (amounts.length === 1)
        return amounts[0];
    return { kind: 'all', amounts };
}
export function firstAmount(...amounts) {
    if (amounts.length === 0)
        return unit();
    if (amounts.length === 1)
        return amounts[0];
    return { kind: 'first', amounts };
}
export function cappedAmount(amount, maxAmount) {
    return { kind: 'capped', amount, maxAmount: { kind: 'const', value: maxAmount } };
}
export function cappedAmountBy(amount, maxAmount) {
    return { kind: 'capped', amount, maxAmount };
}
export function surchargeAmount(taxonomy, rate, description, amount) {
    return surchargeAmountBy(taxonomy, { kind: 'const', value: rate }, description, amount);
}
export function surchargeAmountBy(taxonomy, rate, description, amount) {
    return {
        kind: 'surcharge',
        rate,
        description,
        classification: { kind: 'taxonomy', ...taxonomy },
        amount,
    };
}
export function describeAmount(description, amount) {
    return { kind: 'describe', description, amount };
}
export function categoriseAmount(taxonomy, description, amount) {
    return {
        kind: 'describe',
        description,
        classification: { kind: 'taxonomy', ...taxonomy },
        amount,
    };
}
export function chargeTypeAmount(chargeType, description, amount) {
    return {
        kind: 'describe',
        description,
        classification: { kind: 'chargeType', chargeType },
        amount,
    };
}
export function zero() {
    return { kind: 'zero' };
}
export function once(date, amount, currency = 'USD') {
    return { kind: 'once', date, amount, currency };
}
export function repeat(recurrence, amount, currency, startDate, endDate, maxOccurrences) {
    const result = {
        kind: 'repeat',
        recurrence,
        amount,
        currency,
        startDate: typeof startDate === 'string' ? { kind: 'const', value: startDate } : startDate,
    };
    if (endDate !== undefined) {
        result.endDate = endDate;
    }
    if (maxOccurrences !== undefined) {
        result.maxOccurrences = maxOccurrences;
    }
    return result;
}
export function literalStart(payment) {
    return literalDate(payment.startDate);
}
export function asDueDate(date) {
    switch (date.kind) {
        case 'const':
        case 'context':
            return date;
        case 'addDays':
            return date.base.kind === 'const' || date.base.kind === 'context'
                ? { kind: 'addDays', base: date.base, days: date.days }
                : undefined;
        case 'startOfMonth':
        case 'endOfMonth':
        case 'dayOfMonth':
            return undefined;
    }
}
export function literalDate(date) {
    switch (date.kind) {
        case 'const':
            return date.value;
        case 'context':
            return undefined;
        case 'addDays':
            return date.base.kind === 'const' ? addDaysToDate(date.base.value, date.days) : undefined;
    }
}
export function allPayments(...payments) {
    if (payments.length === 0)
        return zero();
    if (payments.length === 1)
        return payments[0];
    return { kind: 'all', payments };
}
export function firstPayment(...payments) {
    if (payments.length === 0)
        return zero();
    if (payments.length === 1)
        return payments[0];
    return { kind: 'first', payments };
}
export function notToExceed(payment, maxAmount, period) {
    return { kind: 'notToExceed', payment, maxAmount, period };
}
export function withinCap(payments, maxAmount, period) {
    if (typeof maxAmount !== 'number' || !Number.isFinite(maxAmount) || maxAmount < 0) {
        throw new TypeError(`A notToExceed ceiling must be a finite amount, not ${String(maxAmount)}`);
    }
    const spent = new Map();
    const kept = [];
    for (const payment of [...payments].sort((a, b) => compareDates(a.dueDate, b.dueDate))) {
        if (payment.amount <= 0) {
            kept.push(payment);
            continue;
        }
        const key = period === 'calendarYear' ? payment.dueDate.slice(0, 4) : 'term';
        const before = spent.get(key) ?? 0;
        const amount = Math.min(payment.amount, Math.max(0, maxAmount - before));
        spent.set(key, before + amount);
        if (amount > 0)
            kept.push({ ...payment, amount });
    }
    return kept;
}
export function oneTime(dueDate, dueAmount, currency = 'USD') {
    return once(dueDate, scaleAmount(dueAmount, unit()), currency);
}
export function constCond(value) {
    return { kind: 'const', value };
}
export function after(date) {
    return { kind: 'after', date };
}
export function before(date) {
    return { kind: 'before', date };
}
export function onOrAfter(date) {
    return { kind: 'onOrAfter', date };
}
export function onOrBefore(date) {
    return { kind: 'onOrBefore', date };
}
export function isLate(gracePeriod = 0) {
    return { kind: 'isLate', gracePeriod };
}
export function and(...conditions) {
    if (conditions.length === 0)
        return constCond(true);
    if (conditions.length === 1)
        return conditions[0];
    return { kind: 'and', conditions };
}
export function or(...conditions) {
    if (conditions.length === 0)
        return constCond(false);
    if (conditions.length === 1)
        return conditions[0];
    return { kind: 'or', conditions };
}
export function not(condition) {
    return { kind: 'not', condition };
}
export function context(factName) {
    return { kind: 'context', factName };
}
export function constDate(value) {
    return { kind: 'const', value };
}
export function dayOfMonth(day, monthOffset = 0) {
    return { kind: 'dayOfMonth', day, monthOffset };
}
export function startOfMonth(monthOffset = 0) {
    return { kind: 'startOfMonth', monthOffset };
}
export function endOfMonth(monthOffset = 0) {
    return { kind: 'endOfMonth', monthOffset };
}
export function addDays(base, days) {
    return { kind: 'addDays', base, days };
}
export function contextDate(dateName) {
    return { kind: 'context', dateName };
}
export function constNum(value) {
    return { kind: 'const', value };
}
export function measure(factName) {
    return { kind: 'context', factName };
}
export function daysLate(gracePeriod = 0) {
    return { kind: 'daysLate', gracePeriod };
}
export function multiply(left, right) {
    return { kind: 'multiply', left, right };
}
export function maxOf(...values) {
    return { kind: 'max', values };
}
export function minOf(...values) {
    return { kind: 'min', values };
}
//# sourceMappingURL=constructors.js.map